import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, Grid, Tooltip, Typography } from "@mui/material";
import SegmentOutlinedIcon from "@mui/icons-material/SegmentOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { FormProvider, RHFFileField, RHFSelectField, RHFTextField } from "../../common/hook-form";
import RHFTextArea from "../../common/hook-form/RHFTextArea";
import { apiConstants, postGeneric } from "../../common/constants";
import { getMerchant } from "../../helpers/ReduxHelpers";
import { PRIMARY_BLUE } from '../../styles/foundation/colors';
import SectionCard from "./SectionCard";
import TicketDateRangeField from "./TicketDateRangeField";
import { getCategoryConfig } from "./escalationConfigCache";
import { DATE_RANGE_PATTERN, fieldKind } from "./ticketFieldKinds";
import { gradientBtnSx, resetBtnSx } from "./ticketFormStyles";

// Several AWBs in one form raise one ticket each; more than this goes through Bulk Create.
const MAX_AWBS = 20;
const parseAwbs = (value) => [...new Set((value || "").split(/[\s,]+/).map((a) => a.trim()).filter(Boolean))];

/** The AWBs typed so far as chips — one ticket each — with any the last check refused in red. */
const AwbPreview = ({ value, problems }) => {
    const awbs = parseAwbs(value);
    if (awbs.length === 0) return null;
    const bad = awbs.filter((a) => problems[a]).length;
    return (
        <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12, color: bad ? "#C62828" : "#637381", mb: 0.5, fontWeight: 600 }}>
                {awbs.length} AWB{awbs.length === 1 ? "" : "s"} → {awbs.length} ticket{awbs.length === 1 ? "" : "s"}
                {awbs.length > MAX_AWBS ? ` · up to ${MAX_AWBS} at a time` : ""}
                {bad ? ` · ${bad} can't take a ticket` : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {awbs.map((a) => (
                    <Tooltip key={a} title={problems[a] ? `${a} ${problems[a]}` : ""} arrow>
                        <Chip size="small" label={a} variant={problems[a] ? "filled" : "outlined"}
                            sx={{ fontSize: 12, fontWeight: 600, ...(problems[a] ? { bgcolor: "#FDECEC", color: "#C62828" } : { color: PRIMARY_BLUE, borderColor: "#BFD7F5" }) }} />
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
};

/**
 * Create Ticket — merchant panel. Category/subcategory + SLA + every field to show come live
 * from GET /api/escalation/config/categories, same as the admin panel's Create Ticket (no
 * hardcoded form here, unlike the old freshdesk-backed CreateTicket.js this replaces).
 *
 * No merchant picker (this merchant, always — getMerchant()) and no requester step: a
 * merchant-panel caller IS the external requester, and the backend forces merchantId/
 * requesterType/requesterEmail/requesterName from the logged-in session regardless of what's
 * sent (see EscalationService.createEscalation) — asking for them here would just be
 * decoration nobody reads.
 */
const CreateEscalationTicket = ({ onClose, onCreated }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const merchant = getMerchant();

    // Shared with the list page's filter options, so opening Create Ticket doesn't refetch them.
    useEffect(() => {
        let cancelled = false;
        const fetchCategories = async () => {
            try {
                const categoryConfig = await getCategoryConfig();
                if (!cancelled) setCategories(categoryConfig);
            } catch (error) {
                if (!cancelled) enqueueSnackbar("Could not load the category list", { variant: "error" });
            }
        };
        fetchCategories();
        return () => { cancelled = true; };
    }, [enqueueSnackbar]);

    // The rules depend on the chosen subcategory, so the resolver reads the latest schema from a
    // ref each time. (Assigning methods.resolver after useForm — what this form did before — is
    // ignored by react-hook-form, so the form never validated anything itself.)
    const validationSchemaRef = useRef(Yup.object());
    const methods = useForm({
        resolver: (values, context, options) => yupResolver(validationSchemaRef.current)(values, context, options),
        defaultValues: { category: "", subCategory: "" },
    });
    const { handleSubmit, reset, watch, setValue } = methods;
    // AWB -> why it can't take a ticket, from the last check (shown on the AWB chips).
    const [awbProblems, setAwbProblems] = useState({});

    const selectedCategory = watch("category");
    const selectedSubCategory = watch("subCategory");

    // A subcategory belongs to one category: clear it when the category changes, or the old name
    // still passes "required" and is sent with the new category.
    const prevCategoryRef = useRef(selectedCategory);
    useEffect(() => {
        if (prevCategoryRef.current !== selectedCategory) setValue("subCategory", "");
        prevCategoryRef.current = selectedCategory;
    }, [selectedCategory, setValue]);

    const categoryOptions = useMemo(
        () => [...categories].sort((a, b) => a.categoryName.localeCompare(b.categoryName))
            .map((c) => ({ value: c.categoryName, label: c.categoryName })),
        [categories]
    );

    const subCategoryOptions = useMemo(() => {
        const cat = categories.find((c) => c.categoryName === selectedCategory);
        // Switched-off subcategories (Categories tab) aren't offered for new tickets.
        return (cat?.subCategories || []).filter((s) => s.active !== false).map((s) => ({ value: s.name, label: s.name }));
    }, [categories, selectedCategory]);

    const selectedSubCategoryObj = useMemo(() => {
        const cat = categories.find((c) => c.categoryName === selectedCategory);
        return cat?.subCategories?.find((s) => s.name === selectedSubCategory) || null;
    }, [categories, selectedCategory, selectedSubCategory]);

    // formKey is unique per slot even when the same field name repeats (some subcategories have
    // two separate file fields) — index-suffixed so RHF treats each as an independent input.
    const selectedFields = useMemo(
        () => (selectedSubCategoryObj?.fields || []).map((f, i) => ({
            ...f,
            formKey: `${f.name}__${i}`,
            // Each AWB gets its own ticket (see onSubmit).
            title: f.name === "awbNumber" ? "AWB Number(s) — comma separated, one ticket each" : f.title,
        })),
        [selectedSubCategoryObj]
    );

    const slaLabel = useMemo(() => {
        const fr = selectedSubCategoryObj?.firstResponseMinutes;
        const res = selectedSubCategoryObj?.resolutionMinutes;
        const toH = (m) => (m != null ? Math.round(m / 60) : 4); // matches EscalationConfigService's default fallback (4h/48h)
        return `First response in ${toH(fr)}h · Resolution by ${res != null ? Math.round(res / 60) : 48}h`;
    }, [selectedSubCategoryObj]);

    const dynamicShape = {};
    selectedFields.forEach((f) => {
        if (f.type === "file") {
            dynamicShape[f.formKey] = Yup.mixed().test(
                "required-file",
                `${f.title} is required`,
                (value) => !f.mandatory || !!(value && value.length > 0)
            );
        } else {
            let schema = Yup.string();
            const kind = fieldKind(f);
            if (f.name === "awbNumber") {
                schema = schema.test("awb-count", `Up to ${MAX_AWBS} AWBs at a time — use Bulk Create for more`,
                    (value) => parseAwbs(value).length <= MAX_AWBS);
            } else if (kind === "dateRange") {
                schema = schema.test("date-range", `Pick a start and end date for ${f.title}`,
                    (value) => !value || DATE_RANGE_PATTERN.test(value));
            } else if (kind === "number") {
                schema = schema.test("number", `${f.title} must be a number`,
                    (value) => !value || /^-?\d+(\.\d+)?$/.test(value.trim()));
            } else if (kind === "date") {
                schema = schema.test("date", `Pick a date for ${f.title}`,
                    (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value));
            } else if (kind === "dayCount") {
                schema = schema.test("day-count", "Enter a whole number of days between 1 and 365",
                    (value) => !value || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 365));
            }
            dynamicShape[f.formKey] = f.mandatory ? schema.required(`${f.title} is required`) : schema;
        }
    });

    const validationSchema = Yup.object().shape({
        category: Yup.string().required("Please select a category"),
        subCategory: Yup.string().required("Please select a subcategory"),
        ...dynamicShape,
    });
    validationSchemaRef.current = validationSchema;

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            let awbs = [];
            const payload = {
                merchantId: merchant?.id,
                merchantName: merchant?.name,
                category: data.category,
                subCategory: data.subCategory,
            };

            const customFieldValues = {};
            const files = [];
            selectedFields.forEach((f) => {
                const value = data[f.formKey];
                if (f.name === "awbNumber") {
                    awbs = parseAwbs(value);
                } else if (f.name === "description") {
                    payload.description = value || undefined;
                } else if (f.type === "file") {
                    if (value && value[0]) files.push(value[0]);
                } else if (value) {
                    customFieldValues[f.name] = value;
                }
            });
            if (Object.keys(customFieldValues).length > 0) payload.customFieldValues = customFieldValues;

            // Every AWB gets the same checks a ticket does (the merchant's AWB, not delivered unless
            // allowed, no open ticket for it here) before anything is created: the ones that pass get
            // their tickets, the ones that don't stay in the form, marked with the reason.
            let refused = [];
            if (awbs.length > 0) {
                const check = await postGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/check-awbs`,
                    { category: data.category, subCategory: data.subCategory, awbNumbers: awbs });
                refused = (check?.data || []).map((p) => ({ awb: p.awb, reason: p.reason }));
                setAwbProblems(Object.fromEntries(refused.map((p) => [p.awb, p.reason])));
                if (refused.length === awbs.length) {
                    enqueueSnackbar(refused.length === 1
                        ? `AWB ${refused[0].awb} ${refused[0].reason}. No ticket was created.`
                        : `None of the ${awbs.length} AWBs can take a ticket (marked in red). No ticket was created.`,
                        { variant: "error", autoHideDuration: 10000 });
                    return;
                }
            }
            const toCreate = awbs.filter((a) => !refused.some((p) => p.awb === a));

            // One ticket per AWB, one after another (each carries the same attachments); an AWB that
            // fails doesn't stop the rest, and stays in the form to fix and retry.
            const created = [];
            const failed = [];
            for (const awb of (toCreate.length > 0 ? toCreate : [undefined])) {
                const formData = new FormData();
                formData.append("data", JSON.stringify({ ...payload, awbNumber: awb }));
                files.forEach((file) => formData.append("attachments", file));
                try {
                    const res = await postGeneric(apiConstants.ESCALATION_CREATE_V2, formData);
                    created.push(res?.data?.escalationNumber);
                } catch (error) {
                    const reason = error?.response?.data?.message || "Some error occurred while creating the ticket";
                    failed.push({ awb, reason });
                }
            }
            if (created.length > 0) {
                enqueueSnackbar(created.length === 1 ? `Ticket ${created[0]} created` : `${created.length} tickets created: ${created.join(", ")}`,
                    { variant: "success" });
                onCreated && onCreated();
            }
            failed.forEach(({ awb, reason }) => enqueueSnackbar(awb ? `AWB ${awb}: ${reason}` : reason, { variant: "error", autoHideDuration: 10000 }));
            refused.forEach(({ awb, reason }) => enqueueSnackbar(`AWB ${awb} ${reason}. No ticket was created for it.`, { variant: "error", autoHideDuration: 10000 }));
            failed.forEach(({ awb, reason }) => { if (awb) refused.push({ awb, reason }); });
            if (refused.length > 0) setAwbProblems(Object.fromEntries(refused.map((p) => [p.awb, p.reason])));
            if (failed.length === 0 && refused.length === 0) {
                reset();
                setAwbProblems({});
                onClose && onClose();
            } else {
                const awbField = selectedFields.find((f) => f.name === "awbNumber");
                // Only the AWBs still without a ticket stay in the field, to fix and retry.
                if (awbField && created.length > 0) setValue(awbField.formKey, refused.map((x) => x.awb).join(", "));
            }
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Some error occurred while creating the ticket", { variant: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                {selectedCategory && selectedSubCategory && (
                    <Box sx={{
                        display: "flex", alignItems: "center", gap: 0.75, alignSelf: "flex-start",
                        border: "1px solid #F3D9AE", bgcolor: "#FDF3E5", borderRadius: "999px", px: 1.25, py: 0.5,
                    }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: "#B45309" }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#B45309" }}>{slaLabel}</Typography>
                    </Box>
                )}

                <SectionCard icon={<SegmentOutlinedIcon sx={{ color: PRIMARY_BLUE }} />} title="Classification" sub="What is this ticket about?">
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <RHFSelectField label="Category" name="category" options={categoryOptions} width="100%" />
                        </Grid>
                        {selectedCategory && (
                            <Grid item xs={12} md={6}>
                                <RHFSelectField label="Subcategory" name="subCategory" options={subCategoryOptions} width="100%" />
                            </Grid>
                        )}
                    </Grid>
                </SectionCard>

                {selectedCategory && selectedSubCategory && selectedFields.length > 0 && (
                    <SectionCard icon={<AssignmentOutlinedIcon sx={{ color: PRIMARY_BLUE }} />} title="Ticket Details" sub={`Fields for "${selectedSubCategory}"`}>
                        <Grid container spacing={2}>
                            {selectedFields.map((f) => (
                                <Grid item xs={12} md={f.type === "textarea" ? 12 : 6} key={f.formKey}>
                                    {f.type === "textarea" && (
                                        <RHFTextArea name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" rows={4} fullWidth />
                                    )}
                                    {fieldKind(f) === "dateRange" && (
                                        <TicketDateRangeField name={f.formKey} label={f.mandatory ? `${f.title} *` : f.title} />
                                    )}
                                    {fieldKind(f) === "dayCount" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth
                                            type="number" inputProps={{ min: 1, max: 365, step: 1 }} />
                                    )}
                                    {fieldKind(f) === "number" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth
                                            type="number" inputProps={{ step: "any" }} />
                                    )}
                                    {fieldKind(f) === "date" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth
                                            type="date" InputLabelProps={{ shrink: true }} />
                                    )}
                                    {fieldKind(f) === "text" && (
                                        <RHFTextField name={f.formKey} label={f.title} mandatory={f.mandatory} defaultValue="" fullWidth />
                                    )}
                                    {f.name === "awbNumber" && <AwbPreview value={watch(f.formKey)} problems={awbProblems} />}
                                    {f.type === "file" && (
                                        <RHFFileField name={f.formKey} label={f.mandatory ? `${f.title} *` : f.title} />
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                )}

                {!(selectedCategory && selectedSubCategory) && (
                    <Typography sx={{ fontSize: 12.5, color: "#637381", px: 0.5 }}>
                        Select a category and subcategory to load this ticket's fields.
                    </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                    <Button variant="outlined" sx={resetBtnSx} onClick={() => reset()}>Reset</Button>
                    <LoadingButton loading={isLoading} type="submit" variant="contained" sx={gradientBtnSx} startIcon={<AddIcon />}>
                        Create Ticket
                    </LoadingButton>
                </Box>
            </Box>
        </FormProvider>
    );
};

export default CreateEscalationTicket;
